import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { authApi, type LoginRequest, type LoginContent } from "@/api/auth.api";
import { STORAGE_KEYS } from "@/shared/constants";

export const useAuthStore = defineStore("auth", () => {
  // State
  const accessToken = ref<string | null>(
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
  );
  const refreshToken = ref<string | null>(
    localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
  );
  const userInfo = ref<LoginContent | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value);
  const userName = computed(() => userInfo.value?.name || "");

  // Actions
  async function login(loginData: LoginRequest) {
    const response = await authApi.login(loginData);
    const { content } = response.data;

    // 토큰 저장
    accessToken.value = content.accessToken;
    refreshToken.value = content.refreshToken;
    userInfo.value = content;

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, content.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, content.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(content));
  }

  async function logout() {
    try {
      if (accessToken.value && refreshToken.value) {
        await authApi.logout({
          accessToken: accessToken.value,
          refreshToken: refreshToken.value,
        });
      }
    } catch (error) {
      console.error("로그아웃 중 오류:", error);
    } finally {
      clearAuth();
    }
  }

  function clearAuth() {
    accessToken.value = null;
    refreshToken.value = null;
    userInfo.value = null;

    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  }

  async function refreshAccessToken() {
    if (!accessToken.value || !refreshToken.value) {
      throw new Error("토큰이 없습니다");
    }

    const response = await authApi.reissueToken({
      accessToken: accessToken.value,
      refreshToken: refreshToken.value,
    });

    const { content } = response.data;

    accessToken.value = content.accessToken;
    refreshToken.value = content.refreshToken;
    userInfo.value = content;

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, content.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, content.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(content));
  }

  // 초기화: localStorage에서 사용자 정보 복원
  function initAuth() {
    const storedUserInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    if (storedUserInfo) {
      try {
        userInfo.value = JSON.parse(storedUserInfo);
      } catch (error) {
        console.error("사용자 정보 파싱 오류:", error);
        clearAuth();
      }
    }
  }

  // 앱 시작 시 호출
  initAuth();

  return {
    // State
    accessToken,
    refreshToken,
    userInfo,
    // Getters
    isAuthenticated,
    userName,
    // Actions
    login,
    logout,
    clearAuth,
    refreshAccessToken,
  };
});
